import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, ListChecks, Keyboard, BookOpen, Sparkles,
  BarChart3, LogOut, Sun, Moon, Menu, X, GraduationCap,
  Mic, Building2, FilePen, Mail, PhoneCall, ShieldAlert, ClipboardList, FileUp, Users, MessageSquareQuote, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const studentNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/quizzes", label: "Assessments", icon: ListChecks },
  { to: "/bundles", label: "Company Mocks", icon: Building2 },
  { to: "/typing", label: "Typing Test", icon: Keyboard },
  { to: "/versant", label: "Voice & Accent", icon: Mic },
  { to: "/voice-drills", label: "Voice Drills", icon: MessageSquareQuote },
  { to: "/ar-sim", label: "AR Call Sim", icon: PhoneCall },
  { to: "/aml-sim", label: "AML Case Sim", icon: ShieldAlert },
  { to: "/courses", label: "Courses", icon: BookOpen },
];
const adminNav = [
  { to: "/admin", label: "Analytics", icon: BarChart3 },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/inbox", label: "Contact Inbox", icon: Inbox },
  { to: "/admin/generate", label: "AI Generator", icon: Sparkles },
  { to: "/admin/editor", label: "Content Editor", icon: FilePen },
  { to: "/admin/import", label: "Import Course", icon: FileUp },
  { to: "/admin/emails", label: "Notifications", icon: Mail },
  { to: "/admin/tasks", label: "Task Board", icon: ClipboardList },
  { to: "/quizzes", label: "Assessments", icon: ListChecks },
  { to: "/courses", label: "Courses", icon: BookOpen },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = user?.role === "admin" ? adminNav : studentNav;

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Elite Hire India</span>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {nav.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-semibold truncate" data-testid="sidebar-username">{user?.name}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-[18px] h-[18px]" /> Logout
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/60 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="hidden lg:block font-heading font-bold text-lg tracking-tight capitalize">
            {location.pathname.split("/")[1] || "dashboard"}
          </div>
          <div className="flex items-center gap-3">
            {user?.target_path && (
              <span className="hidden sm:inline text-xs font-mono px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground uppercase tracking-wider" data-testid="target-path-badge">
                {user.target_path}
              </span>
            )}
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-md">
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
